import React, { createContext, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { abrirAba, fecharAba, gravarAbas, lerAbas, slugDaRota } from '../lib/abasPersonalizadas';
import { PERSONALIZADOS } from './registry';
import PersonalizadoHost from './PersonalizadoHost';
import './abas-personalizadas.css';

// Abas das telas PERSONALIZADAS. Abrir uma tela em /personalizados/:slug cria (ou reativa) a aba dela; cada tela
// aberta fica MONTADA e escondida quando a aba nao e a ativa, entao filtros, resultados e scroll continuam la quando
// a pessoa volta. O estado vive no HomePage (acima do <Outlet/>): sair para o Dashboard e voltar nao fecha nada.
// A primeira aba da barra e FIXA: "Páginas Internas" (/Internas), para voltar a lista sem fechar as telas abertas.
//   AbasPersonalizadasProvider  guarda a lista de abas (e a repoe do sessionStorage no F5)
//   BarraAbas                   desenha a barra e as telas das abas abertas
//   PersonalizadoRota           elemento da rota /personalizados/:slug: so registra a aba
const AbasContext = createContext(null);

const ROTULO_INTERNAS = 'Páginas Internas';

export function AbasPersonalizadasProvider({ userId, children }) {
    const [abas, setAbas] = useState(() => lerAbas(userId, (slug) => !!PERSONALIZADOS[slug]));

    useEffect(() => { gravarAbas(userId, abas); }, [userId, abas]);

    const abrir = useCallback((slug) => {
        if (PERSONALIZADOS[slug]) setAbas(a => abrirAba(a, slug));
    }, []);
    const fechar = useCallback((slug) => setAbas(a => fecharAba(a, slug, null).abas), []);

    const valor = useMemo(() => ({ abas, abrir, fechar }), [abas, abrir, fechar]);
    return <AbasContext.Provider value={valor}>{children}</AbasContext.Provider>;
}

// A tela fica sempre montada dentro do painel; `hidden` so a esconde. memo: trocar de aba nao re-renderiza as outras.
const Painel = memo(function Painel({ slug, ativo }) {
    return (
        <div className="pz-aba-painel" hidden={!ativo}>
            <PersonalizadoHost slug={slug} ativo={ativo} />
        </div>
    );
});

const rotaDe = (slug) => `/personalizados/${encodeURIComponent(slug)}`;

export function BarraAbas() {
    const ctx = useContext(AbasContext);
    const loc = useLocation();
    const navigate = useNavigate();
    const scrollSalvo = useRef({});
    const barraRef = useRef(null);
    const abas = ctx ? ctx.abas : [];
    const slugRota = slugDaRota(loc.pathname);
    const aberta = abas.some(a => a.slug === slugRota) ? slugRota : null;
    // A barra aparece nas telas personalizadas e nas Páginas Internas (de onde se abre outra), e so se houver aba aberta.
    const naInternas = /^\/internas\/?$/i.test(loc.pathname);
    const mostrar = abas.length > 0 && (!!aberta || naInternas);

    // A pagina e uma so para todas as abas: ao ativar uma, devolve o scroll em que ela ficou (ou o topo).
    useLayoutEffect(() => {
        if (aberta) window.scrollTo(0, scrollSalvo.current[aberta] || 0);
    }, [aberta]);

    // Muitas abas: a barra rola na horizontal, entao traz a ativa para dentro dela (mexe so no scroll da barra, nunca no da pagina).
    useEffect(() => {
        const barra = barraRef.current;
        const on = barra && barra.querySelector('.pz-aba.on');
        if (!on) return;
        const ini = on.offsetLeft;
        const fim = ini + on.offsetWidth;
        if (ini < barra.scrollLeft) barra.scrollLeft = Math.max(0, ini - 8);
        else if (fim > barra.scrollLeft + barra.clientWidth) barra.scrollLeft = fim - barra.clientWidth + 8;
    }, [aberta, naInternas, abas.length]);

    // A barra nao mostra scrollbar: a roda do mouse sobre ela rola as abas na horizontal (so quando ha o que rolar).
    useEffect(() => {
        const barra = barraRef.current;
        if (!barra) return undefined;
        const roda = (e) => {
            if (barra.scrollWidth <= barra.clientWidth || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
            e.preventDefault();
            barra.scrollLeft += e.deltaY;
        };
        barra.addEventListener('wheel', roda, { passive: false });
        return () => barra.removeEventListener('wheel', roda);
    }, [mostrar]);

    // Enquanto a barra aparece o <body> leva `pz-com-abas`: e ela que define `--pz-abas-h` para as telas que fixam algo abaixo da topbar.
    useEffect(() => {
        if (!mostrar) return undefined;
        document.body.classList.add('pz-com-abas');
        return () => document.body.classList.remove('pz-com-abas');
    }, [mostrar]);

    if (!ctx) return null;

    const guardarScroll = () => { if (aberta) scrollSalvo.current[aberta] = window.scrollY; };
    const ir = (slug) => {
        if (slug === aberta) return;
        guardarScroll();
        navigate(rotaDe(slug));
    };
    const irInternas = () => {
        if (naInternas) return;
        guardarScroll();
        navigate('/Internas');
    };
    const fecharDaBarra = (slug) => {
        const { proxima } = fecharAba(abas, slug, aberta);
        guardarScroll();
        delete scrollSalvo.current[slug];
        ctx.fechar(slug);
        if (slug === aberta) navigate(proxima ? rotaDe(proxima) : '/Internas');
    };

    return (
        <>
            {mostrar && (
                <div className="pz-abas" role="tablist" aria-label="Telas abertas" ref={barraRef}>
                    <div className={'pz-aba pz-aba-fixa' + (naInternas ? ' on' : '')} role="presentation">
                        <button type="button" role="tab" aria-selected={naInternas} className="pz-aba-nome" title={ROTULO_INTERNAS}
                            onClick={irInternas}>
                            {ROTULO_INTERNAS}
                        </button>
                    </div>
                    {abas.map(a => {
                        const titulo = (PERSONALIZADOS[a.slug] || {}).titulo || a.slug;
                        const on = a.slug === aberta;
                        return (
                            <div key={a.slug} className={'pz-aba' + (on ? ' on' : '')} role="presentation">
                                <button type="button" role="tab" aria-selected={on} className="pz-aba-nome" title={titulo}
                                    onClick={() => ir(a.slug)}
                                    onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); fecharDaBarra(a.slug); } }}>
                                    {titulo}
                                </button>
                                <button type="button" className="pz-aba-x" aria-label={`Fechar ${titulo}`} title="Fechar aba"
                                    onClick={() => fecharDaBarra(a.slug)}>×</button>
                            </div>
                        );
                    })}
                </div>
            )}
            {abas.filter(a => a.montada).map(a => <Painel key={a.slug} slug={a.slug} ativo={a.slug === aberta} />)}
        </>
    );
}

// Elemento da rota /personalizados/:slug (dentro do PermissionGate): so registra a aba; quem desenha a tela e a BarraAbas.
// Slug que nao existe no registry segue mostrando "Tela nao encontrada"; sem Provider acima, abre a tela direto (comportamento antigo).
export function PersonalizadoRota() {
    const { slug } = useParams();
    const ctx = useContext(AbasContext);
    const abrir = ctx ? ctx.abrir : null;
    const conhecida = !!PERSONALIZADOS[slug];

    useEffect(() => { if (abrir && conhecida) abrir(slug); }, [abrir, conhecida, slug]);

    if (!ctx || !conhecida) return <PersonalizadoHost slug={slug} />;
    return null;
}
